import type { RequestOptions } from '@@/plugin-request/request';
import type { RequestConfig } from '@umijs/max';
import { message, notification } from 'antd';
import { BIZ_ERROR_NOTIFIED, showApiErrorMessage } from '@/utils/apiMessage';
import {
  getAuthToken,
  handleUnauthorized,
  isAuthErrorCode,
} from '@/utils/authSession';

/** 套餐过期业务码（与后端 PackageExpiredException::CODE 一致） */
export const PACKAGE_EXPIRED_CODE = 402;

/** 分享页等场景：不携带 Authorization */
const PUBLIC_API_PATHS = [
  '/api/login',
  '/api/login/captcha',
  '/api/doc',
  '/api/sign',
  '/api/site/getSetting',
  '/api/external/getInclusionShare',
  '/api/external/scheduleArticleKeywords',
  '/api/mediaRelease/preview',
];

const isPublicApiRequest = (url?: string, publicApi?: boolean): boolean => {
  if (publicApi) {
    return true;
  }
  if (!url) {
    return false;
  }
  const path = url.split('?')[0];
  return PUBLIC_API_PATHS.some((p) => path === p || path.endsWith(p));
};

const markBizErrorNotified = (error: { [key: string]: unknown }) => {
  error[BIZ_ERROR_NOTIFIED] = true;
};

const handleBizErrorCode = (error: { info?: { code?: number; msg?: string }; [key: string]: unknown }) => {
  const errorInfo = error.info;
  if (!errorInfo) {
    return;
  }

  const { msg, code } = errorInfo;
  markBizErrorNotified(error);

  if (code === 500) {
    notification.error({
      message: '系统错误',
      description: msg || '系统发生错误，请稍后重试',
    });
    return;
  }

  if (isAuthErrorCode(code)) {
    handleUnauthorized(msg || '登录已过期，请重新登录');
    return;
  }

  if (code === 403) {
    showApiErrorMessage(code, '您没有权限执行此操作');
    return;
  }

  if (code === PACKAGE_EXPIRED_CODE) {
    showApiErrorMessage(code, msg || '套餐已过期，该功能无法使用，请续费');
    return;
  }

  if (code === 0) {
    showApiErrorMessage(code, msg || '操作失败');
    return;
  }

  showApiErrorMessage(code ?? 'unknown', msg || '请求失败');
};

/**
 * @name 错误处理
 * GEO系统的错误处理方案
 * code: 1=成功, 0=失败, 402=套餐过期, 500=系统错误
 * @doc https://umijs.org/docs/max/request#配置
 */
export const errorConfig: RequestConfig = {
  errorConfig: {
    errorThrower: (res) => {
      const { code, msg, data } = res as { code?: number; msg?: string; data?: unknown };

      if (code !== 1) {
        const error: Record<string, unknown> & { name: string; message: string; info: object } = {
          name: 'BizError',
          message: msg || '请求失败',
          info: { code, msg, data },
        };
        throw error;
      }
    },

    errorHandler: (error: any, opts: any) => {
      if (opts?.skipErrorHandler) throw error;

      if (error.name === 'BizError') {
        handleBizErrorCode(error);
        return;
      }

      if (error.response) {
        const status = error.response.status;

        switch (status) {
          case 401:
            handleUnauthorized('登录已过期，请重新登录');
            break;
          case 403:
            showApiErrorMessage(status, '您没有权限访问');
            break;
          case 404:
            showApiErrorMessage(status, '请求的资源不存在');
            break;
          case 500:
            showApiErrorMessage(status, '服务器错误');
            break;
          default:
            showApiErrorMessage(status, `请求错误: ${status}`);
        }
      } else if (error.request) {
        showApiErrorMessage('network', '网络错误，请检查网络连接');
      } else if (error.message) {
        showApiErrorMessage('client', error.message || '请求失败，请重试');
      }
    },
  },

  requestInterceptors: [
    (config: RequestOptions) => {
      const publicApi = (config as RequestOptions & { publicApi?: boolean }).publicApi;
      if (isPublicApiRequest(config.url, publicApi)) {
        return config;
      }
      const token = getAuthToken();
      if (token) {
        config.headers = {
          ...config.headers,
          Authorization: `Bearer ${token}`,
        };
      }
      return config;
    },
  ],

  responseInterceptors: [],

  withCredentials: true,
};
